// Angular imports
import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';

// Third party imports
import DataSource from 'devextreme/data/data_source';
import ArrayStore from 'devextreme/data/array_store';
import { DxFileUploaderComponent, DxFormComponent } from 'devextreme-angular';
import notify from 'devextreme/ui/notify';
import { custom } from 'devextreme/ui/dialog';

// CC-Common imports
import { IconConstants, Utils } from 'cc-common';

// Component imports
import { PropertyCommunityDocumentsComponent } from '../../../../management-common/component/common/property-community-documents/property-community-documents.component';

// Constant imports
import { Constants } from '../../../../_constants/constants';
import { EmailConstants } from '../../../../_constants/email-constants';
import { MessageInterval } from '../../../../_constants/messages-intervals';

// Service imports
import { ARMiscInvoiceService } from '../../_services/account-receivable/ar-misc-invoice.service';
import { ServerErrorService } from '../../../../_services/shared-services/server-error.service';
import { PrecisionService } from '../../../../_services/shared-services/precision.service';
import { HelperService } from '../../../../_services/shared-services/helper.service';
import { FileReaderService } from '../../../../_services/shared-services/file-reader.service';
import { ContextManagerService } from '../../../../_services/data-services/context-manager.service';

// Model imports
import { ARMiscInvoiceEditorResultDto } from '../../_models/account-receivable/ar-misc-invoice/ar-misc-invoice-editor-result-dto';
import { ARPostMiscInvoiceParametersDto } from '../../_models/account-receivable/ar-misc-invoice/ar-post-misc-invoice-parameters-dto';
import { ARMiscInvoiceTypeResultDto } from '../../_models/account-receivable/ar-misc-invoice/ar-misc-invoice-type-result-dto';
import { ARMiscInvoiceSubcategoriesParameterDto } from '../../_models/account-receivable/ar-misc-invoice/ar-misc-invoice-subcategories-parameter-dto';
import { GLSubcategorySelectorResultDto } from '../../_models/common/gl-account/glsubcategory-selector-result-dto';
import { DocumentAttachmentRawDto } from '../../_models/account-receivable/ar-misc-invoice/document-attachment-raw-dto';
import { ARMiscInvoicePaymentExistsParameterDto } from '../../_models/account-receivable/ar-misc-invoice/ar-misc-invoice-payment-exists-parameter-dto';
import { KeyValueResultDto } from '../../../../_models/key-value-result-dto';

@Component({
    selector: 'app-ar-misc-invoice-editor',
    templateUrl: './ar-misc-invoice-editor.component.html',
    styleUrls: ['./ar-misc-invoice-editor.component.scss']
})
export class ARMiscInvoiceEditorComponent implements OnInit {
    @ViewChild('miscInvoiceEditorForm', { static: false }) miscInvoiceEditorForm: DxFormComponent;
    @ViewChild('fileUploader', { static: false }) fileUploader: DxFileUploaderComponent;
    @ViewChild('propertyCommunityDocuments', { static: false })
    propertyCommunityDocuments: PropertyCommunityDocumentsComponent;
    @Input() visible: boolean;
    @Input() managementCompanyId: number;
    @Input() communityId: number;
    @Input() customerId: string;
    @Input() customerName: string;
    @Input() popUpTitle = '';
    @Input() showInvoiceDetailOnly = false;
    @Input() nsfChargeText: string;
    @Output() closeMiscEditor = new EventEmitter<boolean>();
    @Output() getInvoiceData = new EventEmitter<any>();

    defaultLabelLocation: string = Constants.defaultFormLabelLocation;

    loadingVisible = false;
    currencyFormat: any;
    closeIcon: string = IconConstants.CLOSE;
    saveIcon: string = IconConstants.SAVE;
    attachmentIcon = IconConstants.ATTACHMENT;

    miscInvoiceUngroupedData: DataSource;
    miscInvoiceEditorData: ARMiscInvoiceEditorResultDto;
    miscInvoiceParam: ARPostMiscInvoiceParametersDto = new ARPostMiscInvoiceParametersDto();
    subCategories: GLSubcategorySelectorResultDto[] = [];
    subLedgers: KeyValueResultDto[] = [];
    entityUsesSubCategory: boolean;
    today = new Date();

    defaultDescription: string;
    checkNumber = '';
    userCanOverrideFee = false;
    isIncludePaymentDetail = false;
    accountLabelHasDefaultSubLedger: boolean;

    remainingCharacters = 1000;
    attachmentOptions: any[];
    selectedAttachementOption: string;

    isPayableToManagementCompany: boolean;
    supportingDocument: DocumentAttachmentRawDto = new DocumentAttachmentRawDto();

    constructor(
        private readonly errorService: ServerErrorService,
        private readonly arMiscInvoiceService: ARMiscInvoiceService,
        private readonly precisionService: PrecisionService,
        private readonly helperService: HelperService,
        private readonly fileReaderService: FileReaderService,
        private readonly contextSrvice: ContextManagerService
    ) {
        this.currencyFormat = this.precisionService.getCurrencyFormat();
    }

    ngOnInit() {
        const selectedContext = this.contextSrvice.getSelectedContext();
        if (!!selectedContext) {
            this.managementCompanyId = this.managementCompanyId || selectedContext.managementCompanyId;
            this.communityId = this.communityId || selectedContext.communityId;
            this.customerId = this.customerId || selectedContext.customerId;
            this.customerName = this.customerName || selectedContext.ownerName;
        }
        if (!!this.customerId && !!this.customerName) {
            this.popUpTitle = `Add Invoice for ${this.customerName} (${this.customerId})`;
        }
        if (!!this.communityId && !!this.managementCompanyId) {
            this.getMiscInvoiceEditor();
        }
        this.prepareAttachmentOptions();
    }

    onHidden(): void {
        this.visible = false;
        this.closeMiscEditor.emit(false);
    }

    onInvoiceTypeSelection(data: ARMiscInvoiceTypeResultDto): void {
        if (
            Utils.notNullAndDefined(data) &&
            (Utils.isNullOrUndefined(this.miscInvoiceParam.invoiceTypeId) ||
                Utils.notEquals(this.miscInvoiceParam.invoiceTypeId, data.miscInvoiceTypeId))
        ) {
            this.miscInvoiceParam.invoiceTypeId = data.miscInvoiceTypeId;
            this.miscInvoiceParam.amount = data.defaultFee;
            this.defaultDescription = data.defaultDescription;
            this.userCanOverrideFee = data.userCanOverrideFee;
            this.isPayableToManagementCompany = data.isPayableToManagementCompany;
            this.miscInvoiceParam.subLedgerId = data.defaultSubLedgerId;
            this.accountLabelHasDefaultSubLedger = data.accountLabelHasDefaultSubLedger;
            if (this.entityUsesSubCategory) {
                this.getMiscInvoiceSubcategories();
            }
        }
    }

    onPaymentDetailChanged(event): void {
        if (!event.value) {
            this.miscInvoiceParam.checkNumber = null;
            this.miscInvoiceParam.paymentAmountReceived = null;
        }
    }

    onTextAreaKeyUp(e): void {
        if (!!e.event && e.event.target.textLength > 0) {
            setTimeout(() => {
                this.remainingCharacters = 1000 - e.event.target.textLength;
            }, 50);
        }
    }

    onAttachDocuments(attachments: any[] = []): void {
        if (!!attachments && attachments.length > 0) {
            const attachedDocument: DocumentAttachmentRawDto = new DocumentAttachmentRawDto();
            attachedDocument.fileBase64String = null;
            attachedDocument.customerId = this.customerId;
            attachedDocument.communityId = this.communityId;
            attachedDocument.fileName = attachments[0].fileName;
            attachedDocument.fileId = attachments[0].fileId;
            if (!!attachments[0].customerId && !!attachments[0].communityId) {
                attachedDocument.mode = 'OWNER';
            } else {
                attachedDocument.mode = 'COMMUNITY';
            }
            this.supportingDocument = attachedDocument;
        }
    }

    onAttachmentsSelect(event): void {
        const localAttachments = event.value;
        if (!!localAttachments && localAttachments.length > 0) {
            const attachedDocument: DocumentAttachmentRawDto = new DocumentAttachmentRawDto();
            this.fileReaderService.readFileToBase64(localAttachments[0]).subscribe((base64Data) => {
                attachedDocument.fileBase64String = base64Data;
            });
            attachedDocument.customerId = this.customerId;
            attachedDocument.communityId = this.communityId;
            attachedDocument.fileName = localAttachments[0].name;
            attachedDocument.mode = 'LOCAL';
            this.supportingDocument = attachedDocument;
        }
    }

    onAttachmentClick(event): void {
        const selectedSize = 0;
        switch (event.itemData) {
            case EmailConstants.ATTACH_LOCAL_FILE:
                const uploader: any = this.fileUploader.instance;
                uploader._isCustomClickEvent = true;
                uploader._$fileInput[0].click();
                break;
            case EmailConstants.ATTACH_COMMUNITY_DOC:
                this.propertyCommunityDocuments.show(event.itemData, this.communityId, null, selectedSize);
                break;
            case EmailConstants.ATTACH_OWNER_DOC:
                this.propertyCommunityDocuments.show(event.itemData, this.communityId, this.customerId, selectedSize);
                break;
        }
    }

    onSaveClick(): void {
        if (!!this.miscInvoiceEditorForm && this.miscInvoiceEditorForm.instance) {
            const validator = this.miscInvoiceEditorForm.instance.validate().isValid;
            if (validator) {
                if (this.showInvoiceDetailOnly) {
                    this.getInvoiceData.emit(this.miscInvoiceParam);
                    this.onHidden();
                } else {
                    if (!!this.isPayableToManagementCompany) {
                        if (!!this.isIncludePaymentDetail) {
                            const customDialog = custom({
                                title: 'Warning',
                                messageHtml: `The fee is payable to the Management Company.<br />
                                        No invoices will be added to the homeowner's account.<br />
                                        The payment should be deposited into the Management <br /> Company account.`,
                                buttons: [
                                    {
                                        text: 'Ok',
                                        type: 'default',
                                        onClick: () => {
                                            this.validateMiscInvoicePayment();
                                        }
                                    }
                                ]
                            });
                            customDialog.show();
                        } else {
                            const customDialog = custom({
                                title: 'Error',
                                messageHtml: `This charge is payable directly to the Management Company. <br /> A payment must always accompany a charge payable to <br /> the Management Company. Please supply the payment <br /> information for this charge.`,
                                buttons: [
                                    {
                                        text: 'Ok',
                                        type: 'default'
                                    }
                                ]
                            });
                            customDialog.show();
                        }
                    } else if (!this.isPayableToManagementCompany) {
                        if (!!this.isIncludePaymentDetail) {
                            const communityMsg = `The payment will be deposited into the community's account.`;
                            const customDialog = custom({
                                title: 'Warning',
                                messageHtml: communityMsg,
                                buttons: [
                                    {
                                        text: 'Ok',
                                        type: 'default',
                                        onClick: () => {
                                            // Note: Below code is as per ASP.NET.
                                            if (this.miscInvoiceParam.amount >= 1000) {
                                                this.checkAmount();
                                            } else {
                                                this.checkIncludePaymentDetail();
                                            }
                                        }
                                    }
                                ]
                            });
                            customDialog.show();
                        } else {
                            // Note: Below code is as per ASP.NET.
                            if (this.miscInvoiceParam.amount >= 1000) {
                                this.checkAmount();
                            } else {
                                this.checkIncludePaymentDetail();
                            }
                        }
                    } else {
                        this.addARMiscInvoice();
                    }
                }
            }
        }
    }

    checkAmount(): void {
        const dialog = this.helperService.getConfirmationDialog(
            'Confirmation',
            '<b> The amount of the invoice or payment is greater than or equal to $1,000. <br /> Are you sure you want to proceed?'
        );
        dialog.show().then((answer: boolean) => {
            if (answer === true) {
                this.checkIncludePaymentDetail();
            }
        });
    }

    checkIncludePaymentDetail(): void {
        if (!!this.isIncludePaymentDetail) {
            this.validateMiscInvoicePayment();
        } else {
            this.addARMiscInvoice();
        }
    }

    private validateMiscInvoicePayment(): void {
        const param: ARMiscInvoicePaymentExistsParameterDto = new ARMiscInvoicePaymentExistsParameterDto();
        param.communityId = this.communityId;
        param.customerId = this.customerId;
        param.managementCompanyId = this.managementCompanyId;
        param.checkNumber = this.miscInvoiceParam.checkNumber;
        param.paymentAmountReceived = this.miscInvoiceParam.paymentAmountReceived;
        this.loadingVisible = true;
        this.arMiscInvoiceService.validateMiscInvoicePayment(param).subscribe(
            () => {
                if (this.miscInvoiceParam.paymentAmountReceived === this.miscInvoiceParam.amount) {
                    this.addARMiscInvoice();
                } else {
                    this.confirmationDialogCheck(
                        '<b> The amount of the payment is different from the invoice amount. <br /> Are you sure you want to proceed?'
                    );
                }
                this.loadingVisible = false;
            },
            (error) => {
                this.loadingVisible = false;
                if (error.status === 400 && !!error.message && error.message.toLowerCase().includes('already exists')) {
                    this.confirmationDialogCheck(
                        `<b> The Check number(${this.miscInvoiceParam.checkNumber}) and the amount($${this.miscInvoiceParam.paymentAmountReceived}) of the payment is same as the invoice amount. <br /> Are you sure you want to proceed?`
                    );
                } else {
                    this.errorService.displayError(error);
                }
            }
        );
    }

    private addARMiscInvoice(): void {
        this.miscInvoiceParam.managementCompanyId = this.managementCompanyId;
        this.miscInvoiceParam.communityId = this.communityId;
        this.miscInvoiceParam.customerId = this.customerId;

        if (Object.keys(this.supportingDocument).length === 0) {
            this.miscInvoiceParam.supportingDocument = null;
        } else {
            this.miscInvoiceParam.supportingDocument = this.supportingDocument;
        }

        this.loadingVisible = true;
        this.arMiscInvoiceService.createARMiscInvoice(this.miscInvoiceParam).subscribe(
            () => {
                notify(
                    'The invoice has been successfully logged. The property notes have been updated.',
                    'success',
                    MessageInterval.successMsgInterval
                );
                this.visible = false;
                this.closeMiscEditor.emit(true);
                this.loadingVisible = false;
            },
            (error) => {
                this.loadingVisible = false;
                this.errorService.displayError(error);
            }
        );
    }

    private getMiscInvoiceEditor(): void {
        this.loadingVisible = true;
        this.arMiscInvoiceService.getARMiscInvoiceEditor(this.communityId, this.managementCompanyId).subscribe(
            (result) => {
                this.miscInvoiceEditorData = result;
                if (!!this.nsfChargeText) {
                    this.miscInvoiceEditorData.invoiceTypes.forEach((inv) => {
                        if (
                            inv.miscInvoiceTypeText.toLowerCase().startsWith('nsf') ===
                            this.nsfChargeText.toLowerCase().startsWith('nsf')
                        ) {
                            this.miscInvoiceParam.invoiceTypeId = inv.miscInvoiceTypeId;
                        }
                    });
                }

                this.miscInvoiceUngroupedData = new DataSource({
                    store: new ArrayStore({
                        data: this.miscInvoiceEditorData.invoiceTypes,
                        key: 'miscInvoiceTypeId'
                    }),
                    group: 'category'
                });
                this.subLedgers = this.miscInvoiceEditorData.subLedgers;
                this.entityUsesSubCategory = this.miscInvoiceEditorData.entityUsesSubCategory;

                this.loadingVisible = false;
                setTimeout(() => {
                    const selectedInvoiceType = this.miscInvoiceEditorData.invoiceTypes.find((type) =>
                        Utils.equals(type.miscInvoiceTypeId, this.miscInvoiceParam.invoiceTypeId)
                    );
                    if (Utils.notNullAndDefined(selectedInvoiceType)) {
                        this.defaultDescription = selectedInvoiceType.defaultDescription;
                        this.userCanOverrideFee = selectedInvoiceType.userCanOverrideFee;
                        this.isPayableToManagementCompany = selectedInvoiceType.isPayableToManagementCompany;
                        this.accountLabelHasDefaultSubLedger = selectedInvoiceType.accountLabelHasDefaultSubLedger;
                        this.miscInvoiceParam.amount = Utils.notNullAndDefined(this.miscInvoiceParam.amount)
                            ? this.miscInvoiceParam.amount
                            : selectedInvoiceType.defaultFee;
                        this.miscInvoiceParam.subLedgerId = Utils.notNullAndDefined(this.miscInvoiceParam.subLedgerId)
                            ? this.miscInvoiceParam.subLedgerId
                            : selectedInvoiceType.defaultSubLedgerId;
                        this.miscInvoiceParam.transactionDate = Utils.notNullOrEmpty(
                            this.miscInvoiceParam.transactionDate
                        )
                            ? this.miscInvoiceParam.transactionDate
                            : this.today;
                        this.miscInvoiceParam.postDate = Utils.notNullOrEmpty(this.miscInvoiceParam.postDate)
                            ? this.miscInvoiceParam.postDate
                            : this.today;
                        this.miscInvoiceParam.dueDate = Utils.notNullOrEmpty(this.miscInvoiceParam.dueDate)
                            ? this.miscInvoiceParam.dueDate
                            : this.today;
                        this.miscInvoiceParam.supportingDocument = new DocumentAttachmentRawDto();

                        if (this.entityUsesSubCategory) {
                            this.getMiscInvoiceSubcategories();
                        }
                    } else {
                        this.miscInvoiceParam.transactionDate = this.today;
                        this.miscInvoiceParam.postDate = this.today;
                        this.miscInvoiceParam.dueDate = this.today;
                        this.miscInvoiceParam.supportingDocument = new DocumentAttachmentRawDto();
                    }
                }, 500);
            },
            (error) => {
                this.loadingVisible = false;
                this.errorService.displayError(error);
            }
        );
    }

    private getMiscInvoiceSubcategories(): void {
        const param: ARMiscInvoiceSubcategoriesParameterDto = new ARMiscInvoiceSubcategoriesParameterDto();
        param.managementCompanyId = this.managementCompanyId;
        param.communityId = this.communityId;
        param.invoiceTypeId = this.miscInvoiceParam.invoiceTypeId;
        setTimeout(() => (this.loadingVisible = true));
        this.arMiscInvoiceService.getARMiscInvoiceSubcategories(param).subscribe(
            (result) => {
                this.subCategories = result.subCategories;
                const selectedSubCat = this.subCategories.find((subCat) =>
                    Utils.equalsIgnoreCase(subCat.code, this.miscInvoiceParam.subCategoryCode)
                );
                if (Utils.notNullAndDefined(selectedSubCat)) {
                    this.miscInvoiceParam.subCategoryCode = selectedSubCat.code;
                } else {
                    this.miscInvoiceParam.subCategoryCode = result.defaultSubCategoryCode;
                }
                this.miscInvoiceEditorForm.instance.repaint();
                setTimeout(() => (this.loadingVisible = false));
            },
            (error) => {
                this.loadingVisible = false;
                this.errorService.displayError(error);
            }
        );
    }

    private prepareAttachmentOptions(): void {
        this.attachmentOptions = [EmailConstants.ATTACH_LOCAL_FILE];
        if (!!this.communityId) {
            this.attachmentOptions.push(EmailConstants.ATTACH_COMMUNITY_DOC);
        }
        if (!!this.customerId) {
            this.attachmentOptions.push(EmailConstants.ATTACH_OWNER_DOC);
        }
    }

    private confirmationDialogCheck(confirmationMsg: string): void {
        const dialog = this.helperService.getConfirmationDialog('Confirmation', confirmationMsg);
        dialog.show().then((answer: boolean) => {
            if (answer === true) {
                this.addARMiscInvoice();
            }
        });
    }
}
